import React from 'react';
import { motion } from 'framer-motion';
import { Landmark, Calendar, Quote, BookOpen, ScrollText } from 'lucide-react';

const HistorySection = () => {
  return (
    <section className="relative bg-[#fcfaf7] py-24 overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-slate-50/50 -skew-x-12 translate-x-20 z-0" />
      
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          
          {/* Left Side: Header & Years */}
          <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-24">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 text-amber-600 mb-4">
                <ScrollText className="w-5 h-5" />
                <span className="text-[10px] font-bold uppercase tracking-[0.4em]">Historical Archives</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-serif font-bold text-slate-900 leading-[1.1]">
                A Legacy of <br />
                <span className="text-amber-600">Faithful Growth</span>
              </h1>
            </motion.div>

            <div className="hidden lg:block space-y-4">
               <div className="flex items-center gap-6 group">
                  <div className="h-px w-12 bg-slate-200 group-hover:w-20 group-hover:bg-amber-500 transition-all duration-500" />
                  <span className="text-4xl font-serif font-bold text-slate-200 group-hover:text-slate-900 transition-colors">1999</span>
               </div>
               <div className="flex items-center gap-6 group">
                  <div className="h-px w-20 bg-amber-500" />
                  <span className="text-4xl font-serif font-bold text-slate-900">Present</span>
               </div>
            </div>

            <div className="p-8 bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 relative">
              <Quote className="absolute top-4 right-6 w-10 h-10 text-slate-50" />
              <p className="text-slate-600 font-serif italic relative z-10">
                "Carved out for effective administration and spiritual expansion in the southern regions of Ibadan."
              </p>
            </div>
          </div>

          {/* Right Side: Narrative Content */}
          <div className="lg:col-span-7">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-12"
            >
              {/* Origin Card */}
              <div className="relative pl-8 md:pl-16 border-l-2 border-amber-500/20">
                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-amber-500 border-4 border-white shadow-sm" />
                
                <div className="flex items-center gap-2 mb-4 text-amber-600">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase tracking-widest">July 1999 • The Genesis</span>
                </div>
                
                <h2 className="text-2xl font-serif font-bold text-slate-900 mb-6 flex items-center gap-3">
                  Our Origins and Milestones
                </h2>
                
                <div className="prose prose-slate prose-lg max-w-none text-slate-600 font-serif leading-relaxed space-y-6">
                  <p>
                    The <strong className="text-slate-900">Diocese of Ibadan South</strong>, a vibrant arm of the Anglican Communion in Nigeria, 
                    was carved out of the Diocese of Ibadan in <span className="text-slate-900 font-bold">July, 1999</span>. 
                    This strategic creation was a divine response to the need for more effective administration 
                    and targeted spiritual growth within the southern reaches of the historic city of Ibadan.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-10">
                    <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                      <Landmark className="text-amber-500 mb-3 w-6 h-6" />
                      <h4 className="font-bold text-slate-900 mb-2">First Foundation</h4>
                      <p className="text-sm">Under the leadership of <span className="font-bold">The Rt. Rev. Jacob Ajetunmobi</span>, whose vision laid the cornerstone.</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                      <BookOpen className="text-amber-500 mb-3 w-6 h-6" />
                      <h4 className="font-bold text-slate-900 mb-2">Mission Focus</h4>
                      <p className="text-sm">Expansion through numerous parishes, outreach initiatives, and social impact.</p>
                    </div>
                  </div>

                  <p>
                    Over the decades, the Diocese has blossomed from a fledgling administrative unit into a 
                    spiritual powerhouse. Guided by biblical truth, we remain committed to leading souls to 
                    Christ, promoting unity, and addressing societal needs through robust programs in 
                    <span className="italic"> education, healthcare, and community welfare</span>.
                  </p>
                </div>
              </div>

              {/* Stats/Highlight Footer */}
              <div className="grid grid-cols-3 gap-4 pt-8">
                <div className="text-center p-4">
                  <p className="text-3xl font-serif font-bold text-slate-900">25+</p>
                  <p className="text-[10px] uppercase tracking-tighter text-slate-400 font-bold">Years of Impact</p>
                </div>
                <div className="text-center p-4 border-x border-slate-100">
                  <p className="text-3xl font-serif font-bold text-slate-900">15+</p>
                  <p className="text-[10px] uppercase tracking-tighter text-slate-400 font-bold">Archdeaconries</p>
                </div>
                <div className="text-center p-4">
                  <p className="text-3xl font-serif font-bold text-slate-900">∞</p>
                  <p className="text-[10px] uppercase tracking-tighter text-slate-400 font-bold">Souls Reached</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HistorySection;